import React, { useState } from 'react';

const NinjaDetails = ({ ninjaData }) => {
  const [expandedAlerts, setExpandedAlerts] = useState(new Set());

  // Utility functions
  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp * 1000).toLocaleString();
  };


  const formatUTCToLocal = (utcTimeString) => {
    if (!utcTimeString) return '';
    const date = new Date(utcTimeString);
    return date.toLocaleTimeString();
  };

  const formatLinkSpeed = (linkSpeed) => {
    if (!linkSpeed || linkSpeed === '0') return 'N/A';
    const speedNum = parseInt(linkSpeed);
    if (speedNum >= 1000000000) return `${(speedNum / 1000000000).toFixed(1)} Gbps`;
    if (speedNum >= 1000000) return `${(speedNum / 1000000).toFixed(0)} Mbps`;
    if (speedNum >= 1000) return `${(speedNum / 1000).toFixed(0)} Kbps`;
    return `${speedNum} bps`;
  };

  const toggleAlert = (uid) => {
    setExpandedAlerts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(uid)) {
        newSet.delete(uid);
      } else {
        newSet.add(uid);
      }
      return newSet;
    });
  };

  const formatAlertType = (sourceType) => {
    if (!sourceType) return 'System Alert';
    
    // Convert from API format to readable format
    // e.g., "CONDITION_AGENT_MEMORY" -> "Memory Alert"
    // e.g., "CONDITION_AGENT_CPU" -> "CPU Alert"
    // e.g., "CONDITION_AGENT_DISK" -> "Disk Alert"
    const cleanType = sourceType
      .replace('CONDITION_AGENT_', '')
      .replace(/_/g, ' ')
      .toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
    
    return `${cleanType} Alert`;
  };

  const getAlertColor = (sourceType, threshold) => {
    // Always use red for any alert
    return '#C82333';
  };



  // Handle case when no ninja data is available
  if (!ninjaData) {
    return (
      <div style={{ color: '#fafafa', textAlign: 'center', padding: '2em' }}>
        <h5>No system details available</h5>
        <p>Ninja monitoring data is not available for this server.</p>
      </div>
    );
  }

  const { os, system, processors, volumes, memory, ipAddresses, macAddresses, alerts, network_interfaces } = ninjaData;

  return (
    <div style={{ color: '#fafafa', fontSize: '0.85rem' }}>
      {/* Alerts Section */}
      {alerts && alerts.length > 0 && (
        <div style={{ marginBottom: '1.5em', backgroundColor: '#2D2A40', padding: '1em', borderRadius: '5px' }}>
          <h6 style={{ color: '#22BE63', marginBottom: '0.8em', borderBottom: '1px solid #555', paddingBottom: '0.3em' }}>
            🚨 Active Alerts ({alerts.length})
          </h6>
          
          {alerts.map((alert) => {
            const isExpanded = expandedAlerts.has(alert.uid);
            const threshold = alert.data?.message?.params?.threshold;
            const alertColor = getAlertColor(alert.sourceType, threshold);

            return (
              <div 
                key={alert.uid} 
                style={{ 
                  marginBottom: '1em', 
                  padding: '0.8em', 
                  backgroundColor: '#2a3142', 
                  borderRadius: '5px',
                  border: `1px solid ${alertColor}40`
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5em' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5em', marginBottom: '0.3em' }}>
                      <span style={{ color: alertColor, fontWeight: 'bold' }}>
                        {formatAlertType(alert.sourceType)}
                      </span>
                      {threshold && (
                        <span style={{ 
                          fontSize: '0.7rem', 
                          backgroundColor: '#1e2332', 
                          padding: '0.2em 0.4em', 
                          borderRadius: '3px',
                          color: '#fafafa'
                        }}>
                          {threshold}{alert.data?.message?.params?.unit === 'PERCENT' ? '%' : ''}
                        </span>
                      )}
                      {alert.data?.message?.params?.unit && alert.data.message.params.unit !== 'PERCENT' && (
                        <span style={{ 
                          fontSize: '0.7rem', 
                          backgroundColor: '#1e2332', 
                          padding: '0.2em 0.4em', 
                          borderRadius: '3px',
                          color: '#fafafa'
                        }}>
                          {alert.data.message.params.unit}
                        </span>
                      )}
                    </div>
                    
                    <div style={{ fontSize: '0.75rem', color: '#bbb', marginBottom: '0.5em' }}>
                      {alert.data?.message?.params?.start_date && alert.data?.message?.params?.end_date && (
                        <div>
                          Duration: {formatUTCToLocal(alert.data.message.params.start_date)} - {formatUTCToLocal(alert.data.message.params.end_date)} (Local Time)
                        </div>
                      )}
                    </div>

                    {!isExpanded && (
                      <div style={{ fontSize: '0.8rem', color: '#ddd' }}>
                        {/* Show only first line when collapsed */}
                        {alert.message.split('\r\n')[0]}
                        {alert.message.includes('\r\n') && (
                          <span style={{ color: '#888', fontStyle: 'italic' }}> ...</span>
                        )}
                      </div>
                    )}
                  </div>
                  
                  <button
                    onClick={() => toggleAlert(alert.uid)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#22BE63',
                      cursor: 'pointer',
                      padding: '0.2em',
                      fontSize: '0.8rem'
                    }}
                  >
                    {isExpanded ? '▲' : '▼'}
                  </button>
                </div>

                {isExpanded && (
                  <div style={{ marginTop: '0.8em', paddingTop: '0.8em', borderTop: '1px solid #555' }}>
                    <div style={{ fontSize: '0.8rem', color: '#ddd', marginBottom: '0.8em', whiteSpace: 'pre-wrap' }}>
                      {alert.message}
                    </div>

                    <div style={{ marginTop: '0.8em', fontSize: '0.7rem', color: '#888' }}>
                      Alert ID: {alert.uid.substring(0, 8)}...
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* System Information */}
      <div style={{ marginBottom: '1.5em', backgroundColor: '#2D2A40', padding: '1em', borderRadius: '5px' }}>
        <h6 style={{ color: '#22BE63', marginBottom: '0.8em', borderBottom: '1px solid #555', paddingBottom: '0.3em' }}>
          🖥️ System Information
        </h6>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5em' }}>
          <div><strong>Display Name:</strong> {ninjaData.displayName}</div>
          <div><strong>System Name:</strong> {ninjaData.systemName}</div>
          <div><strong>Manufacturer:</strong> {system?.manufacturer}</div>
          <div><strong>Model:</strong> {system?.model}</div>
          <div><strong>Serial Number:</strong> {system?.serialNumber}</div>
          <div><strong>Domain:</strong> {system?.domain}</div>
          <div><strong>Chassis Type:</strong> {system?.chassisType}</div>
          <div><strong>Status:</strong> 
            <span style={{ color: ninjaData.offline ? '#C82333' : '#22BE63', marginLeft: '0.3em' }}>
              {ninjaData.offline ? 'Offline' : 'Online'}
            </span>
          </div>
        </div>
      </div>

      {/* Operating System */}
      <div style={{ marginBottom: '1.5em', backgroundColor: '#2D2A40', padding: '1em', borderRadius: '5px' }}>
        <h6 style={{ color: '#22BE63', marginBottom: '0.8em', borderBottom: '1px solid #555', paddingBottom: '0.3em' }}>
          💿 Operating System
        </h6>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5em' }}>
          <div><strong>OS:</strong> {os?.name}</div>
          <div><strong>Architecture:</strong> {os?.architecture}</div>
          <div><strong>Build Number:</strong> {os?.buildNumber}</div>
          <div><strong>Release ID:</strong> {os?.releaseId}</div>
          <div><strong>Language:</strong> {os?.language}</div>
          <div><strong>Last Boot:</strong> {formatTimestamp(os?.lastBootTime)}</div>
          <div><strong>Needs Reboot:</strong> 
            <span style={{ color: os?.needsReboot ? '#C82333' : '#22BE63', marginLeft: '0.3em' }}>
              {os?.needsReboot ? 'Yes' : 'No'}
            </span>
          </div>
        </div>
      </div>

      {/* Hardware Specifications */}
      <div style={{ marginBottom: '1.5em', backgroundColor: '#2D2A40', padding: '1em', borderRadius: '5px' }}>
        <h6 style={{ color: '#22BE63', marginBottom: '0.8em', borderBottom: '1px solid #555', paddingBottom: '0.3em' }}>
          ⚙️ Hardware Specifications
        </h6>
        
        {/* Memory Information */}
        <div style={{ marginBottom: '1em', paddingBottom: '0.5em', borderBottom: '1px solid #555' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5em' }}>
            <div><strong>💾 Total Memory:</strong> <span style={{ color: '#22BE63' }}>{formatBytes(memory?.capacity)}</span></div>
            <div><strong>Memory Type:</strong> Physical RAM</div>
          </div>
        </div>

        {/* Processor Information */}
        {processors?.map((processor, index) => (
          <div key={index} style={{ marginBottom: '1em', paddingBottom: '0.5em', borderBottom: index < processors.length - 1 ? '1px solid #555' : 'none' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5em' }}>
              <div style={{ gridColumn: '1 / -1', color: '#22BE63', fontWeight: 'bold' }}>
                🖥️ Processor {processors.length > 1 ? `#${index + 1}` : ''}
              </div>
              <div style={{ gridColumn: '1 / -1' }}><strong>Name:</strong> {processor.name}</div>
              <div><strong>Architecture:</strong> {processor.architecture}</div>
              <div><strong>Physical Cores:</strong> <span style={{ color: '#22BE63' }}>{processor.numCores}</span></div>
              <div><strong>Logical Cores:</strong> <span style={{ color: '#22BE63' }}>{processor.numLogicalCores}</span></div>
              <div><strong>Base Clock:</strong> {(processor.clockSpeed / 1000000000).toFixed(2)} GHz</div>
              <div><strong>Max Clock:</strong> {(processor.maxClockSpeed / 1000000000).toFixed(2)} GHz</div>
              <div><strong>Performance:</strong> 
                <span style={{ color: processor.numCores >= 8 ? '#22BE63' : processor.numCores >= 4 ? '#ffc107' : '#C82333', marginLeft: '0.3em' }}>
                  {processor.numCores >= 8 ? 'High Performance' : processor.numCores >= 4 ? 'Medium Performance' : 'Basic Performance'}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Storage Information */}
      <div style={{ marginBottom: '1.5em', backgroundColor: '#2D2A40', padding: '1em', borderRadius: '5px' }}>
        <h6 style={{ color: '#22BE63', marginBottom: '0.8em', borderBottom: '1px solid #555', paddingBottom: '0.3em' }}>
          💾 Storage Information
        </h6>
        {volumes?.map((volume, index) => (
          <div key={index} style={{ marginBottom: '1em', paddingBottom: '0.5em', borderBottom: '1px solid #555' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5em' }}>
              <div><strong>Drive:</strong> {volume.name} ({volume.fileSystem})</div>
              <div><strong>Device Type:</strong> {volume.deviceType}</div>
              <div><strong>Capacity:</strong> {formatBytes(volume.capacity)}</div>
              <div><strong>Free Space:</strong> <span style={{ color: '#22BE63' }}>{formatBytes(volume.freeSpace)}</span></div>
              <div><strong>Used:</strong> {((volume.capacity - volume.freeSpace) / volume.capacity * 100).toFixed(1)}%</div>
              <div><strong>Serial Number:</strong> {volume.serialNumber}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Physical Disk Information */}
      {ninjaData.disks && ninjaData.disks.length > 0 && (
        <div style={{ marginBottom: '1.5em', backgroundColor: '#2D2A40', padding: '1em', borderRadius: '5px' }}>
          <h6 style={{ color: '#22BE63', marginBottom: '0.8em', borderBottom: '1px solid #555', paddingBottom: '0.3em' }}>
            🗄️ Physical Disk Information
          </h6>
          {ninjaData.disks.map((disk, index) => (
            <div key={index} style={{ marginBottom: '1em', paddingBottom: '0.5em', borderBottom: index < ninjaData.disks.length - 1 ? '1px solid #555' : 'none' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5em' }}>
                <div style={{ gridColumn: '1 / -1', color: '#22BE63', fontWeight: 'bold', marginBottom: '0.5em' }}>
                  🗄️ Disk {ninjaData.disks.length > 1 ? `#${index + 1}` : ''} - {disk.model}
                </div>
                <div><strong>Description:</strong> {disk.description}</div>
                <div><strong>Status:</strong> 
                  <span style={{ color: ['OK', 'live', 'running'].includes(disk.status) ? '#22BE63' : '#C82333', marginLeft: '0.3em' }}>
                    {disk.status}
                  </span>
                </div>
                <div><strong>Size:</strong> <span style={{ color: '#22BE63' }}>{formatBytes(disk.size)}</span></div>
                <div><strong>Interface:</strong> {disk.interfaceType}</div>
                <div><strong>Media Type:</strong> {disk.mediaType}</div>
                <div><strong>Manufacturer:</strong> {disk.manufacturer}</div>
                <div><strong>Serial Number:</strong> 
                  <span style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                    {disk.serialNumber}
                  </span>
                </div>
                <div><strong>Partitions:</strong> {disk.partitionCount}</div>
                <div><strong>Bytes per Sector:</strong> {disk.bytesPerSector}</div>
                <div><strong>SMART Capable:</strong> 
                  <span style={{ color: disk.smartCapable ? '#22BE63' : '#ffc107', marginLeft: '0.3em' }}>
                    {disk.smartCapable ? 'Yes' : 'No'}
                  </span>
                </div>
                <div><strong>Device Name:</strong> 
                  <span style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                    {disk.name}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Network Information */}
      <div style={{ marginBottom: '1.5em', backgroundColor: '#2D2A40', padding: '1em', borderRadius: '5px' }}>
        <h6 style={{ color: '#22BE63', marginBottom: '0.8em', borderBottom: '1px solid #555', paddingBottom: '0.3em' }}>
          🌐 Network Information
        </h6>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1em' }}>
          <div>
            <h6 style={{ color: '#fafafa', marginBottom: '0.5em' }}>IP Addresses</h6>
            <div style={{ maxHeight: '150px', overflowY: 'auto', padding: '0.5em', backgroundColor: '#2a3142', borderRadius: '3px' }}>
              {ipAddresses?.map((ip, index) => (
                <div key={index} style={{ fontFamily: 'monospace', fontSize: '0.75rem', padding: '0.2em', backgroundColor: '#1e2332', margin: '0.2em 0', borderRadius: '2px' }}>
                  {ip}
                </div>
              ))}
            </div>
          </div>
          <div>
            <h6 style={{ color: '#fafafa', marginBottom: '0.5em' }}>MAC Addresses</h6>
            <div style={{ maxHeight: '150px', overflowY: 'auto', padding: '0.5em', backgroundColor: '#2a3142', borderRadius: '3px' }}>
              {macAddresses?.map((mac, index) => (
                <div key={index} style={{ fontFamily: 'monospace', fontSize: '0.75rem', padding: '0.2em', backgroundColor: '#1e2332', margin: '0.2em 0', borderRadius: '2px' }}>
                  {mac}
                </div>
              ))}
            </div>
          </div>
        </div>
        <div style={{ marginTop: '1em', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.5em' }}>
          <div><strong>Public IP:</strong> <span style={{ fontFamily: 'monospace', color: '#22BE63' }}>{ninjaData.publicIP}</span></div>
          <div><strong>Last Contact:</strong> {formatTimestamp(ninjaData.lastContact)}</div>
          <div><strong>Last Update:</strong> {formatTimestamp(ninjaData.lastUpdate)}</div>
        </div>
      </div>

      {/* Network Interfaces */}
      {network_interfaces && network_interfaces.length > 0 && (
        <div style={{ marginBottom: '1.5em', backgroundColor: '#2D2A40', padding: '1em', borderRadius: '5px' }}>
          <h6 style={{ color: '#22BE63', marginBottom: '0.8em', borderBottom: '1px solid #555', paddingBottom: '0.3em' }}>
            🔌 Network Interfaces ({network_interfaces.length})
          </h6>
          <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
            {network_interfaces.map((nic, index) => (
              <div key={index} style={{ 
                marginBottom: '1em', 
                padding: '0.8em', 
                backgroundColor: '#2a3142', 
                borderRadius: '5px',
                borderLeft: `4px solid ${nic.status === 'Up' ? '#22BE63' : '#C82333'}`
              }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5em' }}>
                  <div style={{ gridColumn: '1 / -1', color: '#22BE63', fontWeight: 'bold', marginBottom: '0.5em' }}>
                    🔌 {nic.interfaceName}
                  </div>
                  
                  <div><strong>Status:</strong> 
                    <span style={{ color: nic.status === 'Up' ? '#22BE63' : '#C82333', marginLeft: '0.3em' }}>
                      {nic.status}
                    </span>
                  </div>
                  <div><strong>Interface Type:</strong> {nic.interfaceType}</div>
                  
                  <div><strong>Link Speed:</strong> 
                    <span style={{ color: '#22BE63', marginLeft: '0.3em' }}>
                      {formatLinkSpeed(nic.linkSpeed)}
                    </span>
                  </div>
                  <div><strong>MTU:</strong> {nic.mtu}</div>
                  
                  <div><strong>MAC Address:</strong> 
                    <span style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                      {nic.macAddress?.[0]}
                    </span>
                  </div>
                  <div><strong>Interface Index:</strong> {nic.interfaceIndex}</div>
                </div>

                {/* IP Addresses for this interface */}
                {nic.ipAddress && nic.ipAddress.length > 0 && (
                  <div style={{ marginTop: '0.8em', paddingTop: '0.5em', borderTop: '1px solid #555' }}>
                    <div style={{ fontSize: '0.75rem', fontWeight: 'bold', color: '#fafafa', marginBottom: '0.3em' }}>
                      IP Addresses:
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3em' }}>
                      {nic.ipAddress.map((ip, ipIndex) => (
                        <span key={ipIndex} style={{ 
                          fontFamily: 'monospace', 
                          fontSize: '0.7rem', 
                          backgroundColor: '#1e2332', 
                          padding: '0.2em 0.4em', 
                          borderRadius: '3px',
                          color: ip.includes(':') ? '#ffc107' : '#22BE63' // Different colors for IPv6 vs IPv4
                        }}>
                          {ip}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Network Configuration */}
                <div style={{ marginTop: '0.8em', paddingTop: '0.5em', borderTop: '1px solid #555' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5em', fontSize: '0.8rem' }}>
                    <div><strong>Default Gateway:</strong> 
                      <span style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                        {nic.defaultGateway === '0.0.0.0' ? 'None' : nic.defaultGateway}
                      </span>
                    </div>
                    <div><strong>Subnet Mask:</strong> 
                      <span style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                        {nic.subnetMask}
                      </span>
                    </div>
                    <div><strong>DNS Servers:</strong> 
                      <span style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                        {nic.dnsServers?.split(';')[0] || 'N/A'}
                        {nic.dnsServers?.split(';').length > 1 && ' (+more)'}
                      </span>
                    </div>
                    <div><strong>DNS Hostname:</strong> 
                      <span style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                        {nic.dnsHostName}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Adapter ID (collapsed by default) */}
                <div style={{ marginTop: '0.5em', fontSize: '0.7rem', color: '#888' }}>
                  Adapter: {nic.adapterName}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

    
    </div>
  );
};

export default NinjaDetails;